'use client'

import { useLocale } from '@/components/Link'
import { graphql } from '@/generated/gql'
import { useGetSsoSettingsByCompanyCodeQuery } from '@/generated/graphql'
import { useAnalytics } from '@/hooks/useAnalytics'
import useTenant from '@/hooks/useTenant'
import { useMutation } from '@apollo/client'
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'

import { Auth } from 'aws-amplify'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import '../../../../app/i18n/client'
import ResetPasswordForm, { FormValues } from '../UniversalPasswordForm'
import { useSignUpConfig } from './config'
import SignUpForm, { SignupFormValues } from './form'
import { createClientMetaData, NO_ACCOUNT_FOUND_ERROR, SIGN_UP_DISABLED } from './rules'
import WelcomeScreen from './welcomeScreen'

const signUpPageSignUpMutation = graphql(`
  mutation SignUpPage_SignUpMutation($input: NewUserSignupInput!) {
    signUp(input: $input) {
      success
      resetPasswordEmailSent
    }
  }
`)

enum Stages {
  Initial,
  SignUp,
  SetPassword,
  Complete,
}

export type PageType = 'gm' | 'cc'

export type SignUpData = {
  firstName: string
  lastName: string
  email: string
  phone: string
  companyName?: string
  companyCode?: string
  password: string
  subscribeNewsletter?: boolean
}

const SignUp = () => {
  const { t } = useTranslation()

  const theme = useTheme()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const analytics = useAnalytics()
  const tenant = useTenant()
  const config = useSignUpConfig()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const [runSignUp] = useMutation(signUpPageSignUpMutation)
  const [stage, setStage] = useState(Stages.Initial)
  const [formData, setFormData] = useState<SignUpData>({} as SignUpData)
  const [error, setError] = useState('')
  const [disableResend, setDisableResend] = useState(false)

  const pageType: PageType = searchParams?.get('intent') === 'gm' ? 'gm' : 'cc'

  if (pageType === 'gm' && stage === Stages.Initial) {
    setStage(Stages.SignUp)
  }

  const companyName = searchParams?.get('companyName') || undefined
  const companyCode = searchParams?.get('companyCode') || undefined
  const destination = searchParams?.get('destination') || ''

  const { data: ssoSettingsData, refetch } = useGetSsoSettingsByCompanyCodeQuery({
    variables: {
      companyCode: companyCode || '',
      tenantId: tenant.id,
    },
  })

  const temporarilyDisableResend = () => {
    setDisableResend(true)
    setTimeout(() => {
      setDisableResend(false)
    }, 30000)
  }

  const onSubmit = async ({ password, subscribeNewsletter }: FormValues) => {
    setFormData((prev) => ({ ...prev, subscribeNewsletter }))
    try {
      if (!formData) throw new Error('No data in form submitted')
      const clientMetadata = createClientMetaData(
        { ...formData, subscribeNewsletter },
        pageType,
        destination,
        ssoSettingsData?.ssoSettingsByCompanyCode?.id,
        locale,
      )
      const result = await runSignUp({
        variables: {
          input: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email.trim(),
            locale: clientMetadata.locale,
            password,
            phone: formData.phone,
            tenantId: tenant.id,
            accountId: ssoSettingsData?.ssoSettingsByCompanyCode?.id,
            companyName: companyName ?? formData.companyName,
            subscribeMarketing: subscribeNewsletter,
            destination: clientMetadata.destination,
          },
        },
      })

      if (result.data?.signUp?.resetPasswordEmailSent) {
        setError(
          "An account already exists for this email. We've sent you a reset password email to get started.",
        )
        return
      }

      analytics?.identify({
        account_name: formData.companyName,
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        marketing_opt_in: subscribeNewsletter,
      })

      temporarilyDisableResend()
      setStage(Stages.Complete)
    } catch (error) {
      const typedError = error as Record<string, string>
      let message
      if (typeof typedError?.message === 'string') message = typedError?.message
      else message = String(error)
      setError(message)
    }
  }
  const resendSignup = () => {
    temporarilyDisableResend()
    if (!formData?.email) return setError('No email address found.')

    const clientMetadata = createClientMetaData(
      { ...formData },
      pageType,
      destination,
      ssoSettingsData?.ssoSettingsByCompanyCode?.id,
      locale,
    )

    // Use signup mutation to resend confirmation code
    runSignUp({
      variables: {
        input: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email.trim(),
          locale: clientMetadata.locale,
          password: formData.password,
          phone: formData.phone,
          tenantId: tenant.id,
          accountId: ssoSettingsData?.ssoSettingsByCompanyCode?.id,
          companyName: companyName ?? formData.companyName,
          subscribeMarketing: formData.subscribeNewsletter,
          destination: clientMetadata.destination,
        },
      },
    })
  }
  const initialSignupHandler = async (details: SignupFormValues) => {
    // if we haven't hit this page with a company code param and its the cc flow then we need to check the code entered
    if (!companyCode && pageType === 'cc') {
      const { data } = await refetch({
        companyCode: details.companyCode?.toUpperCase(),
      })

      if (!data?.ssoSettingsByCompanyCode?.id) {
        setError(NO_ACCOUNT_FOUND_ERROR)
        return
      }

      if (data?.ssoSettingsByCompanyCode?.enabled) {
        setError(SIGN_UP_DISABLED)
        return
      }
    }

    if (!details.email) return
    setFormData({ ...details, password: '' })
    setStage(Stages.SetPassword)
  }

  const { title, description, buttonText } = config[pageType]

  return (
    <Stack spacing={0}>
      {stage === Stages.Initial && (
        <WelcomeScreen
          pageType={pageType}
          ssoEnabled={ssoSettingsData?.ssoSettingsByCompanyCode?.enabled}
          accountNotFound={Boolean(companyCode) && !ssoSettingsData?.ssoSettingsByCompanyCode?.id}
          loading={!ssoSettingsData}
          onSubmit={() => setStage(Stages.SignUp)}
        />
      )}

      {stage === Stages.SignUp && (
        <Stack spacing={2} py={8} px={isSmall ? 4 : 6}>
          <SignUpForm
            onSubmit={initialSignupHandler}
            title={title || ''}
            description={description || ''}
            buttonText={buttonText || ''}
            error={error}
            pageType={pageType}
            companyName={companyName}
            companyCode={companyCode}
          />
        </Stack>
      )}

      {stage === Stages.SetPassword && formData?.email && (
        <Stack spacing={2} py={8} px={isSmall ? 4 : 6}>
          <ResetPasswordForm
            submitText={t('authentication:setPassword:submit')}
            showHeaderIcon
            titleText={t('authentication:setPassword:title')}
            isSubmitting={false}
            onSubmit={onSubmit}
            requireCodeInput={false}
            email={formData.email}
            error={error}
            showTerms
          />
        </Stack>
      )}

      {stage === Stages.Complete && (
        <Stack spacing={2} py={8} px={isSmall ? 4 : 6}>
          <Typography textAlign="center" variant="h3" fontWeight="400">
            {t('authentication:verification:title')}
          </Typography>

          <Typography variant="body2" textAlign="center">
            {t('authentication:verification:description')}
          </Typography>

          <Stack justifyContent="center" alignItems="center" direction="row">
            <Typography variant="body2" textAlign="center">
              {t('authentication:verification:resendHelperText')}
            </Typography>

            <Button disabled={disableResend} variant="text" onClick={resendSignup} size="small">
              {t('authentication:verification:resendButton')}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default SignUp
